import { Fragment, useState } from "react";
import { HTMLBarsContainer } from "./BartChart.elements";
import { generateBars } from "./util/generateBars";
import { ConfiguredTooltip } from "../components/ConfiguredTooltip";
 
const spacing = 2
 
export const HTMLBars = ({
  children, 
  className,
  is_grouped = false, 
  grouped_data,
  ...props
}: Record<string, unknown>): JSX.Element => {
 
  className = `html-bars-container ${className || ''}`

  const {
    toggleTooltip,
    data,
    vertical,
    tooltip_ref,
    color,
    axis_settings,
    width,
    height,
    checkIfSelected,
    checkIfFiltered,
    handleClick,
    stacked,
    group_key,
    colors
  } = props || {}
  
 
  if (vertical) className += ' vertical'
  if (!stacked) className += ' inline'
 
  const x_key = axis_settings?.top?.key || axis_settings?.bottom?.key
  const y_key = axis_settings?.left?.key || axis_settings?.right?.key

  if (stacked || group_key) is_grouped = true
  props = {...props, is_grouped, x_key, y_key }
  
  const bars = generateBars(props)
 
  const group_count = Object?.keys(bars || {})?.length || 0
  let group_size = (vertical ? height : width) / group_count
  
  if (group_size < 20) group_size = 20   
  if (group_size > 50) group_size = 50
  group_size = group_size - (spacing * 2)

  const bar_size = group_size - (spacing * 2)
  const chart_width = Math.floor((group_count) * (group_size))
  
  const handleMouseMove = (e: MouseEvent) => {
    const tooltip_el = tooltip_ref?.current as HTMLDivElement | null
    if (!tooltip_el) return
 
    const group = e?.currentTarget?.dataset?.group
    const data_index = e?.currentTarget?.dataset?.index

    const rect = (e.currentTarget as HTMLDivElement)?.getBoundingClientRect()
    const xpos = Math.max(0, e.clientX - (rect?.left ?? 0))
    const ypos = Math.max(0, e.clientY - (rect?.top ?? 0))
 
    const xval = e?.currentTarget?.dataset?.xval
    const yval = e?.currentTarget?.dataset?.yval
 
    tooltip_el.dataset.xval = xval || ''
    tooltip_el.dataset.yval = yval || ''
    
    tooltip_el.dataset.xpos = String(xpos)
    tooltip_el.dataset.ypos = String(ypos)
    
    if (group) tooltip_el.dataset.group = group
    else delete tooltip_el.dataset.group

    if (data_index) tooltip_el.dataset.index = String(data_index)
    else delete tooltip_el.dataset.index
    
  }

  grouped_data = grouped_data || Object.groupBy(data || [], (d) => {
    return d?.[x_key as string]
  })
 
  return <>

    <HTMLBarsContainer className={className} style={{minWidth: `${chart_width}px`}} >
      
      <>{Object.entries(bars)?.map(([x_val, data], i) => {        
   
        const col_item = {
          bar: true,
          x_key,
          [x_key as string]: x_val,
          items: data,
          index: i
        }

        const group_style = !vertical ?
          { 
            left: `${data?.[0]?.left}px`,
            top: 0,
            bottom: 0,
            width: `${bar_size}px`
          } : {
            top: `${data?.[0]?.top}px`,
            height: `${bar_size}px`
          }

        const col_settings = {
          style: group_style,
          'data-id': 'bar-group',
          'data-xval': x_val,
          'data-area': i,
          'data-axis': vertical ? 'left' : 'bottom',
          onMouseMove: handleMouseMove,
          onMouseEnter: () => toggleTooltip?.(true),
          onMouseLeave: () => toggleTooltip?.(false),
          onClick: (e) => handleClick?.(col_item, e),
        }
 
        return (
          <div {...col_settings} key={`${x_val}${new Date().getTime()}`} className="bar-group">
            
            {data?.map((d: Record<string, unknown>, j: number) => {
        
              const {
                height,
                group,
                y_val
              } = d || {}
 
              const cc = color || colors?.[group]
         
              const styles = {
                height: `${height || 0}%`,
                width: `${bar_size - (spacing * 2)}px`,
                backgroundColor: d?.color as string || cc || 'blue',
              }
              
              let bar_class = 'bar'
 
              const item = {
                ...col_item,
                index: j,
                group,
                y_key,
                [y_key as string]: y_val,
                item: d
              }

              const is_filtered = checkIfFiltered?.(item)
              const is_selected = checkIfSelected?.(item)

              if (is_filtered === true) bar_class += ' is-filtered'
              if (is_filtered === false) bar_class += ' is-not-filtered'
              if (is_selected === true) bar_class += ' is-selected'
              if (is_selected === false) bar_class += ' is-not-selected'

               
              let bar_settings = {
                className: bar_class,
            
                onMouseOver: (e) => {
                  const col_el = e?.currentTarget?.parentElement as HTMLDivElement | null
                  if (!col_el) return
                  if (group) col_el.dataset.group = String(group)
                  col_el.dataset.index = String(j)
                  col_el.dataset.yval = String(y_val)
                },
                onMouseOut: (e) => {
                   const col_el = e?.currentTarget?.parentElement as HTMLDivElement | null
                   if (!col_el) return
                   delete col_el.dataset.yval
                   delete col_el.dataset.group
                   delete col_el.dataset.index
                },
                onClick: (e) => handleClick?.(item, e),
                'data-area': 'bar',
                'data-xval': String(x_val),
                'data-yval': String(y_val),
                'data-index': String(j),
              }

              if (is_grouped && group) {
                bar_settings = {
                  ...bar_settings,
                  'data-group': String(group)
                }
              }

              if (is_grouped && group) {
                bar_settings['data-grouped'] =  String(group)
            
              }
       
              return <Fragment key={`bar-fragment-${j}${i}${new Date().getTime()}`}>
                <div style={styles}  {...bar_settings} />
                </Fragment>
              
            })}
          </div>
        )
      })}</>
 
    </HTMLBarsContainer>
    <HTMLBarsTooltip {...props} bars={bars}/> 
  </>
 
}
 
 

const HTMLBarsTooltip = (props: Record<string, unknown>): JSX.Element => {
  
  const {
    bars,
    group_key,
    x_key,
    y_key 
  } = props || {} 

  const [tooltip, setTooltip] = useState<Record<string, unknown> | undefined>(undefined)

  const cur_group_data = tooltip?.xval ? bars?.[String(tooltip?.xval) as keyof typeof bars] : []
  
  let cur_item = tooltip?.yval && cur_group_data?.find(x => String(x?.[y_key]) === String(tooltip?.yval)) || undefined
  const cur_tooltip = {...(tooltip || {}),  item: cur_item, items: cur_group_data}
 
  const { group, index } = cur_tooltip || {}
 
  if (cur_group_data?.length) { 
    if (cur_group_data?.length === 1) cur_item = cur_group_data?.[0] as Record<string, unknown>
    else {
      if (index) cur_item = cur_group_data?.[index as number] as Record<string, unknown>
      else if (group) cur_item = cur_group_data?.find((d: Record<string, unknown>) => String(d?.[group_key as string]) === String(group)) as Record<string, unknown> | undefined
    }
  }
 
  return <ConfiguredTooltip {...{ ...props, tooltip: cur_tooltip, x_key, y_key, setTooltip }} />
}